import { share } from "../data/rating";
import {
  computeStanding,
  eventsSinceAnchor,
  indexInstances,
  standingKey,
} from "../data/standing";
import type { DimensionId, ViewerId } from "../data/model";
import type { Repository } from "../data/repository";
import type { LensProvider, RankEntry } from "./lens";

// The MVP lens: rank a dimension by each person's relative share of the
// evidence-updated standing point (the read, nudged by events since that read).
// With no events the point equals the read, so the default is unchanged.
export class SingleViewerLens implements LensProvider {
  constructor(private readonly repo: Repository) {}

  async rankByDimension(
    viewer: ViewerId,
    dimensionId: DimensionId,
  ): Promise<RankEntry[]> {
    const [tags, instances] = await Promise.all([
      this.repo.listTags({ viewer_id: viewer, dimension_id: dimensionId }),
      this.repo.listInstances({ viewer_id: viewer, dimension_id: dimensionId }),
    ]);
    const byPd = indexInstances(instances);
    const now = Date.now();
    const standings = tags.map((t) => {
      const all = byPd.get(standingKey(t.person_id, dimensionId)) ?? [];
      const since = eventsSinceAnchor(all, t.read_at ?? t.created_at);
      return computeStanding(t.rating, since, now);
    });
    const total = standings.reduce((s, st) => s + st.point, 0);
    return tags
      .map((t, i) => {
        const st = standings[i]!;
        return {
          person_id: t.person_id,
          rating: t.rating,
          point: st.point,
          share: share(st.point, total),
          band: st.band,
          rationale: t.rationale,
        };
      })
      .sort((a, b) => b.point - a.point);
  }
}
